import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axiosInstance, { setRefreshTokenFunction } from '../utils/axios';
import axios from 'axios'; // 특수한 경우를 위해 유지
import getMessage from '../utils/messages';
import systemAlertService from '../services/systemAlertService';
import { API_BASE_URL } from '../utils/common';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [systemAlerts, setSystemAlerts] = useState([]);
  const location = useLocation();
  
  // 시스템 알림 가져오기 함수
  const fetchSystemAlerts = async (userType = 'admin') => {
    try {
      const response = await systemAlertService.getEffectiveAlerts(userType);
      if (response.success) {
        setSystemAlerts(response.data || []);
        return response.data || [];
      }
    } catch (error) {
      console.error('시스템 알림 조회 실패:', error);
    }
    return [];
  };
  
  // 로그인 함수 
  const login = async (id, pw, options = {}) => {
    try {
      const payload = {
        ////ctl: 'Admin',
        // param1: 'login',
        id,
        pw,
        // 강제 로그인 옵션 추가
        forceLogin: options.forceLogin || false,
        // 사용자 시간대 (의심스러운 로그인 시간 감지용)
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      };

      const loginAxios = axios.create({
        baseURL: API_BASE_URL,
        withCredentials: false, // 로그인에서는 쿠키를 전송하지 않음
      });

      const response = await loginAxios.post("/auth/login", payload);

      if (response.data.success) {
        // 사용자 정보 저장
        const userData = {
          idx: response.data.data.idx,
          role: response.data.data.role,
        };
        setUser(userData);

        // 백엔드에서 이미 system_alerts를 포함하여 반환함 (중복 호출 제거)
        // 알림을 먼저 설정한 후 인증 상태를 변경해야 SystemAlertDisplay가 올바르게 동작
        const alerts = response.data.data.system_alerts || [];
        setSystemAlerts(alerts); // SystemAlertDisplay가 구독할 수 있도록 상태 업데이트

        // 토큰은 HTTP-Only 쿠키로 자동 저장됨
        // 인증 상태 변경 (알림 설정 후 변경해야 함)
        setIsAuthenticated(true);

        return {
          success: true,
          systemAlerts: alerts,
          data: {
            role: response.data.data.role,
            password_expired: response.data.data.password_expired,
            password_expiry_warning: response.data.data.password_expiry_warning,
            days_until_password_expiry:
              response.data.data.days_until_password_expiry,
            must_change_password: response.data.data.must_change_password,
            password_change_reason: response.data.data.password_change_reason,
            is_initial_password: response.data.data.is_initial_password,
          },
        };
      } else {
        // 다중 로그인 감지 체크
        if (response.data.code === 409) {
          return {
            success: false,
            message:
              response.data.message || "이미 다른 기기에서 로그인 중입니다.",
            multiLogin: true,
            sessionInfo: response.data.context.active_session,
            options: response.data.options,
          };
        }

        // 메시지 코드 기반 처리
        let message;
        let messageCode = response.data.message;

        if (messageCode && response.data.context) {
          // 메시지 코드와 파라미터가 있으면 getMessage 사용
          message = getMessage(messageCode, response.data.context);
        } else {
          // 기존 방식 메시지 처리
          message = response.data.message || "로그인에 실패했습니다.";
        }

        return {
          success: false,
          message: message,
          messageCode: messageCode,
          messageParams: response.data.context,
        };
      }
    } catch (error) {
      console.error("로그인 오류:", error);
      // 벡엔드에서 MultiLoginException 발생 시 409 오류
      if (error.response && error.response.status === 409) {
        return {
          success: false,
          message: "이미 다른 기기에서 로그인 중입니다.",
          multiLogin: true,
          sessionInfo: error.response.data.value,
        };
      }
      // 401 부터 500까지 모든 서버 오류의 메시지 코드 처리
      if (error.response && error.response.data) {
        const responseData = error.response.data;

        // 메시지 코드 기반 처리
        let message;
        let messageCode = responseData.message;

        if (messageCode && responseData.context) {
          // 메시지 코드와 파라미터가 있으면 getMessage 사용
          message = getMessage(messageCode, responseData.context);
        } else {
          // 기존 방식 메시지 처리
          message = responseData.message || "로그인 중 오류가 발생했습니다.";
        }

        return {
          success: false,
          message: message,
          messageCode: messageCode,
          messageParams: responseData.context,
          statusCode: error.response.status,
        };
      }

      // 기본 에러 처리
      return {
        success: false,
        message: "로그인 중 오류가 발생했습니다.",
      };
    }
  };

  // 로그아웃 함수
  const logout = async () => {
    try {
      await axiosInstance.post("/auth/logout");
      setUser(null);
      setIsAuthenticated(false);
      return { success: true };
    } catch (error) {
      //console.error('로그아웃 실패:', error);
      return { success: false, message: "로그아웃 실패" };
    }
  };

  // 세션 잠금 비밀번호 확인
  const verifyPassword = async (password) => {
    try {
      const response = await axiosInstance.post("/auth/verify-password", {
        password,
      });

      console.log(response.data);

      return {
        success: response.data.success,
        message: response.data.message,
        errorCode: response.data.error_code || null,
      };
    } catch (error) {
      // axios 오류일 경우 HTTP 상태 코드 및 서버 오류 코드 처리
      return {
        success: false,
        message: error.response?.data?.message || "인증 실패",
        errorCode:
          error.response?.data?.error_code || error.response?.status || null,
      };
    }
  };

  // 토큰 갱신 진행 중 여부를 추적하는 플래그
  const isRefreshing = useRef(false);
  // 갱신 실패 시 재시도 횟수 제한을 위한 카운터
  const refreshAttempts = useRef(0);
  const MAX_REFRESH_ATTEMPTS = 2;

  // 토큰 갱신 함수
  const refreshToken = async () => {
    // 이미 갱신 중이거나 최대 재시도 횟수를 초과한 경우 추가 요청 방지
    if (isRefreshing.current) {
      //console.log('토큰 갱신이 이미 진행 중입니다');
      return false;
    }
    
    if (refreshAttempts.current >= MAX_REFRESH_ATTEMPTS) {
      //console.log(`최대 토큰 갱신 시도 횟수(${MAX_REFRESH_ATTEMPTS}회)를 초과했습니다`);
      refreshAttempts.current = 0; // 카운터 리셋
      return false;
    }

    try {
      isRefreshing.current = true;
      refreshAttempts.current++;
      
      // 인터셉터 없는 독립 인스턴스로 요청하여 재귀 방지
      // HTTP-Only 쿠키 전송을 위해 withCredentials: true 설정
      const axiosInstance = axios.create({
        withCredentials: true,
        baseURL: API_BASE_URL,
      });
      const response = await axiosInstance.post('/auth/refresh-token');
      
      const success = response.data.success;
      
      if (success) {
        refreshAttempts.current = 0; // 성공 시 카운터 리셋
      }
      
      return success;
    } catch (error) {
      //console.error('토큰 갱신 실패:', error);
      return false;
    } finally {
      isRefreshing.current = false;
    }
  };

  // 수정 제안: 페이지 이동마다 확인하지 않고, 필요한 시점에만 갱신
  const refreshUserInfo = useCallback(async () => {
    try {
      const userResponse = await axios.get(`${API_BASE_URL}/auth/role`);
      if (userResponse.data.success) {
        setUser(userResponse.data.data || {});
      }
    } catch (error) {
      // 오류 처리
    }
  }, []);

  // 사용자 인증 확인 및 권한 가져오기 함수
  const checkAuth = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_BASE_URL}/auth/status`);
      
      if (response.data.success) {
        setIsAuthenticated(true);
        // /auth/status가 이제 사용자 정보도 반환하므로 별도 호출 불필요
        if (response.data.data) {
          setUser(response.data.data);
        } else {
          // fallback: 사용자 정보가 없으면 /auth/role 호출
          refreshUserInfo();
        }
      } else {
        setIsAuthenticated(false);
        setUser(null);
      }
    } catch (error) {
      // 인증 실패 또는 토큰 만료
      //console.error('인증 확인 실패:', error);
      setIsAuthenticated(false);
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [refreshUserInfo]);

  // 앱 시작 시 인증 확인
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // axios에 토큰 갱신 함수 등록
  useEffect(() => {
    // axios 인터셉터에 토큰 갱신 함수 등록
    setRefreshTokenFunction(refreshToken);
    
    return () => {
      // 정리 시 토큰 갱신 함수 제거
      setRefreshTokenFunction(null);
    };
  }, [refreshToken]);

  const value = {
    isAuthenticated,
    user,
    isLoading,
    systemAlerts,
    login,
    logout,
    refreshToken,
    checkAuth,
    verifyPassword,
    fetchSystemAlerts,
    role: user?.role || ''
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
